import { useEffect, useMemo, useState } from 'react';
import { LoaderCircle, Search, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useMedicines } from '@/api/hooks/useMedicine';
import { useSubstitutes, useUpdateSubstitutes } from '@/api/hooks/useSubstitutes';
import type { Medicine } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { money } from '@/lib/utils';

/**
 * Alternates for when this medicine is out of stock: composition-matched
 * suggestions the server finds automatically, plus a manual list the
 * pharmacist curates for cases the automatic match misses (a differently
 * named combination brand, a preferred substitute).
 */
export function SubstitutesDialog({
  medicine,
  open,
  onOpenChange,
}: {
  medicine: Medicine | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useSubstitutes(medicine?.id);
  const updateSubstitutes = useUpdateSubstitutes();

  const [editing, setEditing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pickerQuery, setPickerQuery] = useState('');

  const { data: catalogue } = useMedicines({ limit: 500 }, editing);

  useEffect(() => {
    if (open) setEditing(false);
  }, [open]);

  useEffect(() => {
    if (editing && data) {
      setSelectedIds(new Set(data.data.filter((s) => s.source === 'manual').map((s) => s.id)));
    }
  }, [editing, data]);

  const pickerResults = useMemo(() => {
    if (!catalogue || !medicine) return [];
    const query = pickerQuery.trim().toLowerCase();
    return catalogue.items
      .filter((m) => m.id !== medicine.id)
      .filter((m) => !query || m.name.toLowerCase().includes(query))
      .slice(0, 30);
  }, [catalogue, pickerQuery, medicine]);

  if (!medicine) return null;

  async function handleSave() {
    try {
      await updateSubstitutes.mutateAsync({ id: medicine!.id, substitutes: [...selectedIds] });
      toast({ title: 'Substitutes updated', variant: 'success' });
      setEditing(false);
    } catch {
      toast({ title: 'Could not save substitutes', variant: 'destructive' });
    }
  }

  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('substitutesTitle')} — {medicine.name}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : editing ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                placeholder={t('substitutesSearchPlaceholder')}
                className="pl-9"
              />
            </div>

            {selectedIds.size > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {[...selectedIds].map((id) => {
                  const found = catalogue?.items.find((m) => m.id === id);
                  return (
                    <Badge key={id} variant="secondary" className="gap-1 pr-1">
                      {found?.name ?? id}
                      <button type="button" onClick={() => toggle(id)} className="ml-0.5 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <div className="max-h-64 space-y-0.5 overflow-y-auto rounded-md border border-border p-1">
              {pickerResults.map((m: Medicine) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m.id)}
                  className={`flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-secondary ${
                    selectedIds.has(m.id) ? 'bg-primary/10' : ''
                  }`}
                >
                  <span className="truncate">{m.name}</span>
                  <span className="tabular text-xs text-muted-foreground">{money(m.price)}</span>
                </button>
              ))}
              {pickerResults.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('noData')}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {data && data.data.length > 0 ? (
              data.data.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between gap-3 rounded-md px-2 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{entry.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {entry.stock} {entry.unit} in stock
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {entry.source === 'manual' ? (
                      <Badge variant="secondary">{t('substitutesLinked')}</Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1">
                        <Sparkles className="h-2.5 w-2.5" /> {t('substitutesAuto')}
                      </Badge>
                    )}
                    <span className="tabular text-sm font-semibold">{money(entry.price)}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t('substitutesEmpty')}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {editing ? (
            <>
              <Button type="button" variant="outline" onClick={() => setEditing(false)}>
                {t('cancel')}
              </Button>
              <Button type="button" onClick={handleSave} disabled={updateSubstitutes.isPending}>
                {updateSubstitutes.isPending && <LoaderCircle className="animate-spin" />}
                Save links
              </Button>
            </>
          ) : (
            <Can permission={PERMISSIONS.medicinesEdit}>
              <Button type="button" variant="outline" onClick={() => setEditing(true)}>
                Manage linked substitutes
              </Button>
            </Can>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
